import React, { useCallback, useEffect, useState } from "react";
import { useFlag } from "@unleash/proxy-client-react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @ts-ignore: @types/react-scrolllock doesn't exist yet
import ScrollLock, { TouchScrollable } from "react-scrolllock";
import OutsideClickHandler from "react-outside-click-handler";

import { useQuery, useMutation } from "@apollo/client";
import { header } from "../../../styles/variables";
import {
  GET_AUTH,
  GET_LOCAL_SHOPPING_CART,
  GET_MEMBER_LAYOUT_INFO,
  GET_MEMBER_SHOPPING_CART,
  GET_MEMBER_SUBSCRIPTION,
} from "../../../graphql/queries";
import { TOGGLE_SHOPPING_CART_SUBSCRIPTION } from "../../../graphql/mutations";
import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES, UNLEASH_GUEST_CHECKOUT } from "../../../helpers/constants";
import { showErrorAlert } from "../../../helpers/alerts";
import { getBuildYourOwnIntent } from "../../../helpers/buildYourOwn";
import { useDeviceSize } from "../../../helpers/hooks";
import { extractNumberFromString, checkIfScrollLockInSideTrayShouldBeActive } from "../../../helpers/tools";

import BoxTrayHolder from "../../molecules/BoxTrayHolder";
import { LocalShoppingCart } from "../../../types/__generated__/LocalShoppingCart";
import { ToggleShoppingCartSubscription } from "../../../types/__generated__/ToggleShoppingCartSubscription";
import { getAuth } from "../../../types/__generated__/getAuth";
import { isLoggedIn } from "../../../helpers/auth";
import { countLocalCartAmounts, isDisplayableLocalCartItem } from "../../../helpers/localCartSummary";
import {
  clearGuestBoxIntent,
  hasGuestBoxIntent,
  markGuestBoxIntent,
  onGuestBoxIntentChange,
} from "../../../helpers/guestCheckout";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import BoxCartTogglesController from "../../molecules/BoxCartTogglesController";
import BoxAndCartTrayMobileBottomSection from "../../atoms/BoxAndCartTrayMobileBottomSection";
import { MemberLayoutInfo } from "../../../types/__generated__/MemberLayoutInfo";
import urlPatterns from "../../../urls";

import { HolderInner, SmDownBackdrop, StyledBoxTray } from "./styles";

const BoxTrayCartContent = React.lazy(() => import("../../molecules/BoxTrayCartContent"));

interface BoxAndCartTrayProps {
  heightOfAnnouncement: number;
  shouldOpenBoxAndCartTray?: boolean;
  setShouldOpenBoxAndCartTray: (shouldOpenBoxAndCartTray: boolean) => void;
  isHeaderShown: boolean;
  setHeaderDisplay: (HeaderDisplay: boolean) => void;
  setBoxCartTrayDisplay: (BoxCartTrayDisplay: boolean) => void;
  showHeaderBarFromChild: boolean;
  showWelcomePackWarning?: boolean;
  toggleWelcomePackWarning: () => void;
  shouldWinesBeAddedToSubscription: boolean;
  totalPriceOnToggleButton: number;
  memberLayoutInfoData: MemberLayoutInfo | undefined;
  /** The page renders its own header (hideHeader), so the closed mobile strip
   * anchors to the top of the viewport instead of below the standard one. */
  pageHasOwnHeader?: boolean;
}

const BoxAndCartTray = ({
  heightOfAnnouncement,
  shouldOpenBoxAndCartTray = false,
  setShouldOpenBoxAndCartTray,
  isHeaderShown,
  setHeaderDisplay,
  setBoxCartTrayDisplay,
  showHeaderBarFromChild,
  showWelcomePackWarning = false,
  toggleWelcomePackWarning,
  shouldWinesBeAddedToSubscription,
  totalPriceOnToggleButton,
  memberLayoutInfoData,
  pageHasOwnHeader = false,
}: BoxAndCartTrayProps) => {
  const isGuestCheckoutEnabled = useFlag(UNLEASH_GUEST_CHECKOUT);
  const isUserLoggedIn = isLoggedIn();
  const isGuestCartEnabled = isGuestCheckoutEnabled && !isUserLoggedIn;
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isCartBeingLoaded, setIsCartBeingLoaded] = useState<boolean>(false);
  const [isBoxBeingLoaded, setIsBoxBeingLoaded] = useState<boolean>(false);
  // Guests carry their box-or-cart framing in the local guestBoxIntent marker —
  // the box is just the local cart until checkout turns it into a subscription.
  const [isMonthlyBoxSelected, setIsMonthlyBoxSelected] = useState<boolean>(
    isGuestCartEnabled ? hasGuestBoxIntent() : shouldWinesBeAddedToSubscription
  );

  useEffect(() => {
    setIsMonthlyBoxSelected(isGuestCartEnabled ? hasGuestBoxIntent() : shouldWinesBeAddedToSubscription);
  }, [shouldWinesBeAddedToSubscription, isGuestCartEnabled]);

  // The add-bottle chooser can set the intent while this tray is mounted (it is —
  // Layout keeps it mounted), so follow marker changes live to keep the collapsed
  // side chips in step, and re-read on open as a backup.
  useEffect(() => {
    if (!isGuestCartEnabled) return undefined;
    return onGuestBoxIntentChange(() => setIsMonthlyBoxSelected(hasGuestBoxIntent()));
  }, [isGuestCartEnabled]);

  useEffect(() => {
    if (isOpened && isGuestCartEnabled) {
      setIsMonthlyBoxSelected(hasGuestBoxIntent());
    }
  }, [isOpened, isGuestCartEnabled]);

  let isBox = isMonthlyBoxSelected;
  const { isSmall } = useDeviceSize();
  const history = useHistory();

  // In order to not to use optimisticResponse, which is slow,
  // we track the loading state to keep the toggle quick and responsive
  if (isCartBeingLoaded || isBoxBeingLoaded) {
    isBox = isBoxBeingLoaded;
  }
  const { data: authData } = useQuery<getAuth>(GET_AUTH, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const { data: localShoppingCartData } = useQuery<LocalShoppingCart>(GET_LOCAL_SHOPPING_CART, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const { me } = memberLayoutInfoData || {};
  const boxAmountBottles = me?.subscription?.amountBottles ?? 0;
  const boxAmountPresents = me?.subscription?.amountPresents ?? 0;
  // Guest counts mirror the member cart's server-side amounts, so beers and merch count too.
  const localCartAmounts = countLocalCartAmounts(localShoppingCartData?.localShoppingCart?.items);
  const cartAmountBottles = isUserLoggedIn
    ? me?.shoppingCart?.amountBottles ?? 0
    : localCartAmounts.amountBottles;
  const cartAmountPresents = isUserLoggedIn
    ? me?.shoppingCart?.amountPresents ?? 0
    : localCartAmounts.amountPresents;

  useEffect(() => {
    if (shouldOpenBoxAndCartTray) {
      setTimeout(() => {
        handleChange();
      }, 1000);
    }
  }, [shouldOpenBoxAndCartTray]);

  useEffect(() => {
    isSmall && setIsOpened(false);
  }, [history?.length]);

  const handleChange = () => {
    setHeaderDisplay(true);
    setBoxCartTrayDisplay(!isOpened);
    setIsOpened(!isOpened);
    setShouldOpenBoxAndCartTray(false);
  };

  const handleBoxCartToggleLoading = (boxLoading: boolean, cartLoading: boolean) => {
    setIsBoxBeingLoaded(boxLoading);
    setIsCartBeingLoaded(cartLoading);
  };

  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const [toggleShoppingCartSubscription] = useMutation<ToggleShoppingCartSubscription>(
    TOGGLE_SHOPPING_CART_SUBSCRIPTION,
    {
      refetchQueries: [
        { query: GET_MEMBER_SHOPPING_CART },
        { query: GET_MEMBER_SUBSCRIPTION },
        { query: GET_MEMBER_LAYOUT_INFO },
      ],
      awaitRefetchQueries: true,
    }
  );

  const handleBoxCartToggle = async (isMonthlyBox: boolean) => {
    const isClickingOnActiveToggle = isMonthlyBox === isBox;
    const shouldCloseTray = isOpened && isClickingOnActiveToggle;
    const isLoading = isBoxBeingLoaded || isCartBeingLoaded;

    if (shouldCloseTray) {
      handleCloseTray();
      return;
    }

    if (!isOpened) {
      if (isClickingOnActiveToggle) {
        handleChange();
      } else {
        setTimeout(() => handleChange(), 300);
      }
    }

    if (!isClickingOnActiveToggle) {
      if (isGuestCartEnabled && !memberId) {
        /* A build-your-own calendar is a one-off order, and a guest's "box" is
         * this same local cart: reframed as a box, its wines would be added to
         * past the one calendar it holds. The calendar has to go first. */
        if (isMonthlyBox && getBuildYourOwnIntent()) {
          showErrorAlert(t("common:buildYourOwn.oneOffOnly"), { autoClose: 10000 });
          return;
        }
        // Guests flip the framing locally — no server cart to toggle and no login
        // bounce; the box becomes a real subscription at checkout (DEV-4539).
        if (isMonthlyBox) {
          markGuestBoxIntent();
        } else {
          clearGuestBoxIntent();
        }
        setIsMonthlyBoxSelected(isMonthlyBox);
      } else if (isMonthlyBox && !memberId) {
        history.push(urlPatterns.LOGIN);
      } else if (!isLoading) {
        setIsMonthlyBoxSelected(isMonthlyBox);
        handleBoxCartToggleLoading(isMonthlyBox, !isMonthlyBox);
        await toggleShoppingCartSubscription({
          variables: {
            input: {
              memberId,
            },
          },
        });
        handleBoxCartToggleLoading(false, false);
      }
    }
  };

  /**
   * Calculates the top position of box and cart tray and height offset,
   * for smUp and mdUp screen sizes.
   *
   * @returns {Object}
   * */
  const getTopAndHeightInfo = () => {
    const isHeaderDisplayed = isHeaderShown || showHeaderBarFromChild;

    const smUpTop = isHeaderDisplayed
      ? `${extractNumberFromString(header.mobileHeight) + heightOfAnnouncement}px`
      : `${heightOfAnnouncement}px`;

    const mdUpTop = isHeaderDisplayed
      ? `${extractNumberFromString(header.desktopHeight) + heightOfAnnouncement}px`
      : `${heightOfAnnouncement}px`;

    const smUpHeightOffset = isHeaderDisplayed
      ? `${extractNumberFromString(header.mobileHeight) + heightOfAnnouncement}px`
      : `${heightOfAnnouncement}px`;

    const mdUpHeightOffset = isHeaderDisplayed
      ? `${extractNumberFromString(header.desktopHeight) + heightOfAnnouncement}px`
      : `${heightOfAnnouncement}px`;

    return {
      smUpTop,
      mdUpTop,
      smUpHeightOffset,
      mdUpHeightOffset,
    };
  };

  /**
   * This function closes the box/cart tray.
   * */
  const handleCloseTray = useCallback(() => {
    // Keeps the tray open if the showWelcomePackWarning was visible.
    const closeTray = showWelcomePackWarning;

    setHeaderDisplay(true);
    setBoxCartTrayDisplay(closeTray);
    setIsOpened(closeTray);
  }, [showWelcomePackWarning, setHeaderDisplay, setBoxCartTrayDisplay]);

  /**
   * Handles the outside click handle,
   * and only closes the tray for smUp screens.
   * */
  const handleOutsideClick = () => {
    if (!isSmall) {
      handleCloseTray();
    }
  };

  const { smUpTop, mdUpTop, smUpHeightOffset, mdUpHeightOffset } = getTopAndHeightInfo();

  const shouldScrollLockBeActive = checkIfScrollLockInSideTrayShouldBeActive(isOpened);

  const memberId = authData?.auth?.memberId;

  // Any line the tray lists (wines, beers, merch, gifts, packs...) keeps it mounted for guests.
  const localCartItems = localShoppingCartData?.localShoppingCart?.items?.filter(isDisplayableLocalCartItem);

  // Doesn't render the box and cart tray button if user is not logged in and has no shopping cart items
  if ((isGuestCartEnabled || !isBox) && !isUserLoggedIn && !localCartItems?.length) {
    return null;
  }

  // Under guest box framing the local items ARE the box, so the toggles show the
  // local counts on the box side; the rendered body stays the local cart either way.
  const isGuestBoxFraming = isGuestCartEnabled && isBox;
  const displayedBoxAmountBottles = isGuestBoxFraming ? cartAmountBottles : boxAmountBottles;
  const displayedBoxAmountPresents = isGuestBoxFraming ? cartAmountPresents : boxAmountPresents;
  const displayedCartAmountBottles = isGuestBoxFraming ? 0 : cartAmountBottles;
  const displayedCartAmountPresents = isGuestBoxFraming ? 0 : cartAmountPresents;

  const shouldDisplayNewToggleOutsideBoxTray = !isOpened && !isSmall;

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <StyledBoxTray
        data-testid="box-cart-tray"
        isOpened={isOpened}
        smUpTop={smUpTop}
        mdUpTop={mdUpTop}
        smUpHeightOffset={smUpHeightOffset}
        mdUpHeightOffset={mdUpHeightOffset}
      >
        {shouldDisplayNewToggleOutsideBoxTray && (
          <BoxCartTogglesController
            variant={isSmall ? "header" : "side"}
            onBoxCartToggle={handleBoxCartToggle}
            onCloseTray={handleCloseTray}
            isBox={isBox}
            isTrayOpen={isOpened}
            boxAmountBottles={displayedBoxAmountBottles}
            boxAmountPresents={displayedBoxAmountPresents}
            cartAmountBottles={displayedCartAmountBottles}
            cartAmountPresents={displayedCartAmountPresents}
          />
        )}
        <HolderInner
          isOpened={isOpened}
          isHeaderShown={isHeaderShown || showHeaderBarFromChild}
          pageHasOwnHeader={pageHasOwnHeader}
        >
          <ScrollLock isActive={shouldScrollLockBeActive}>
            <TouchScrollable>
              <BoxTrayHolder>
                <BoxCartTogglesController
                  variant={"header"}
                  onBoxCartToggle={handleBoxCartToggle}
                  onCloseTray={handleCloseTray}
                  isBox={isBox}
                  isTrayOpen={isOpened}
                  boxAmountBottles={displayedBoxAmountBottles}
                  boxAmountPresents={displayedBoxAmountPresents}
                  cartAmountBottles={displayedCartAmountBottles}
                  cartAmountPresents={displayedCartAmountPresents}
                />
                {isOpened && (
                  <React.Suspense fallback={<StyledLoading />}>
                    <BoxTrayCartContent
                      // Guests always render the local cart body — their "box" IS the
                      // local cart; only the toggles/toast carry the box framing.
                      isBox={isGuestCartEnabled ? false : isBox}
                      isBoxBeingLoaded={isBoxBeingLoaded}
                      isCartBeingLoaded={isCartBeingLoaded}
                      memberId={memberId}
                      toggleWelcomePackWarning={toggleWelcomePackWarning}
                      onCloseTray={handleCloseTray}
                    />
                  </React.Suspense>
                )}
              </BoxTrayHolder>
            </TouchScrollable>
            <BoxAndCartTrayMobileBottomSection
              onCloseTray={handleCloseTray}
              isBox={isBox}
              price={totalPriceOnToggleButton}
            />
          </ScrollLock>
          <SmDownBackdrop isOpened={isOpened} onClick={handleOutsideClick} />
        </HolderInner>
      </StyledBoxTray>
    </OutsideClickHandler>
  );
};

export default BoxAndCartTray;
